import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import Spinner from 'components/spinner/Spinner';

import SOVQueriesInputCheckbox from 'modules/insights/sov/components/queries/inputs/checkbox/SOVQueriesInputCheckbox';
import SOVQueriesInputFilter from 'modules/insights/sov/components/queries/inputs/field/filter/SOVQueriesInputFilter';

import { sanitizeValue } from 'modules/insights/sov/utils/sov';
import {
  fetchChannels,
  updateFilteredChannels,
  updateNonFilteredChannels
} from 'modules/insights/sov/reducers/queries/consumer-insights/channels/sov-queries-channels';

import { __t, applyTranslations } from 'utils/language';
import { SINGLE } from 'modules/insights/sov/utils/queries/sov-queries';
import { prepareQueryResultsForChannelsEndpoint } from 'modules/insights/sov/utils/queries/channels/sov-queries-channels';

import './sov-queries-modal-step-related-channels.scss';

import type { CheckboxesListItem } from 'types';
import type {
  SOVQueryChannelsLocal,
  SOVQueryLocal,
  SOVQuerySearchResultsEndpoint,
  SOVQuerySearchResultsLocal,
  SOVQueryStep,
  SOVQueryStepType
} from 'modules/insights/sov/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('modules/insights/sov/components/queries/modals/sov-queries-modal.i18n.json'));

type OwnProps = {
  reportId: number,
  query: SOVQueryLocal,
  isMulti: boolean,
  stepDirection: number,
  stepInfo: SOVQueryStep,
  onSetActionButtonDisabledStatus: (a: boolean) => void
};

type StateProps = {
  channels: SOVQueryChannelsLocal,
  results: SOVQuerySearchResultsLocal,
  resultsCustom: SOVQuerySearchResultsLocal,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchChannels: (
    d: number,
    c: number | undefined | null,
    b: Partial<SOVQuerySearchResultsEndpoint>,
    a: SOVQueryStepType
  ) => Promise<void>,
  dispatchUpdateFilteredChannels: (b: Array<CheckboxesListItem>, a: SOVQueryStepType) => void,
  dispatchUpdateNonFilteredChannels: (c: CheckboxesListItem, b: SOVQueryStepType, a?: boolean | null) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  filterValue: string
};

const MODAL_MSG_KEY = 'sov_queries_modal';

class SOVQueriesModalStepRelatedChannels extends React.PureComponent<Props, State> {
  state = {
    filterValue: ''
  };

  componentDidMount() {
    const {
      reportId,
      query,
      results,
      resultsCustom,
      stepDirection,
      dispatchFetchChannels,
      onSetActionButtonDisabledStatus
    } = this.props;

    const stepType = this.getStepType();
    const activeResultsStandard = results[stepType] || {};
    const activeResultsCustom = resultsCustom[stepType] || {};
    const preparedResults = prepareQueryResultsForChannelsEndpoint(activeResultsStandard, activeResultsCustom);

    if (stepDirection === -1) {
      onSetActionButtonDisabledStatus(false);
    }

    if (stepDirection !== -1) {
      dispatchFetchChannels(reportId, query.id, preparedResults, stepType)
        .then(() => onSetActionButtonDisabledStatus(false));
    }
  }

  getStepType(): SOVQueryStepType {
    const { isMulti, stepInfo } = this.props;

    return !isMulti ? SINGLE : stepInfo.stepType;
  }

  getActiveChannels(): Array<CheckboxesListItem> {
    const { channels } = this.props;
    const stepType = this.getStepType();

    return channels[stepType] || [];
  }

  getAllCheckedStatus(): boolean {
    const activeChannels = this.getActiveChannels();
    const filteredChannels = this.filterChannels(activeChannels);

    // filter mode
    if (activeChannels.length !== filteredChannels.length) {
      return !(filteredChannels.some(item => !item.isSelected));
    }

    return !(activeChannels.some(item => !item.isSelected));
  }

  getChannelName = (channel: CheckboxesListItem): string | undefined | null => {
    const label = channel?.label || '';
    const match = (typeof label == 'string' && label.match(/(^@\w+)\s/)) ?? null;

    return match ? match[0] : null;
  };

  filterChannels(channels: Array<CheckboxesListItem>): Array<CheckboxesListItem> {
    const { filterValue } = this.state;

    if (isEmpty(filterValue)) {
      return channels;
    }

    return channels.filter(channel => (
      this.getChannelName(channel)?.includes(filterValue)
    ));
  }

  highlightMatches(filteredChannels: Array<CheckboxesListItem>): Array<CheckboxesListItem> {
    const { filterValue } = this.state;

    if (isEmpty(filterValue)) {
      return filteredChannels;
    }

    return filteredChannels.map(channel => {
      const channelName = this.getChannelName(channel);

      if (isNil(channelName)) {
        return channel;
      }

      const nonHighlightText = ((typeof channel?.label === 'string' ? channel?.label : '') ?? '')
        .replace(channelName, '');
      const channelNameParts = channelName.split(filterValue);
      const HighlightedText = this.renderHightlightedText(filterValue);

      return {
        ...channel,
        label: (
          <React.Fragment>
            {channelNameParts[0]}
            {HighlightedText}
            {channelNameParts[1]}
            {nonHighlightText}
          </React.Fragment>
        )
      };
    });
  }

  handleToggleFiltered(filteredChannels: Array<CheckboxesListItem>): void {
    const { dispatchUpdateFilteredChannels } = this.props;
    const stepType = this.getStepType();

    dispatchUpdateFilteredChannels(filteredChannels, stepType);
  }

  handleToggleAll = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    const { dispatchUpdateNonFilteredChannels } = this.props;
    const stepType = this.getStepType();
    const activeChannels = this.getActiveChannels();
    const filteredChannels = this.filterChannels(activeChannels);

    // filter mode
    if (activeChannels.length !== filteredChannels.length) {
      this.handleToggleFiltered(filteredChannels);
      return;
    }

    dispatchUpdateNonFilteredChannels(item, stepType, !item.isSelected);
  };

  handleToggleItem = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    const { dispatchUpdateNonFilteredChannels } = this.props;
    const stepType = this.getStepType();

    dispatchUpdateNonFilteredChannels(item, stepType);
  };

  handleFilterChange = (value: string): void => {
    this.setState({
      filterValue: sanitizeValue(value)
    });
  };

  renderNotFound(): React.ReactNode {
    return (
      <div className="sov-queries-modal-step-related-channels__not-found">
        {__t(`${MODAL_MSG_KEY}.messages.no_channel_found`)}
      </div>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="sov-queries-modal-step-related-channels__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderHightlightedText(text: string): React.ReactNode {
    return (
      <span className="sov-queries-modal-step-related-channels__highlighted">
        {text}
      </span>
    );
  }

  renderSelectAll(): React.ReactNode | undefined | null {
    const { filterValue } = this.state;
    const id = 'sov-queries-modal-step-related-channels__select-all';
    const allChecked = this.getAllCheckedStatus();
    const activeChannels = this.getActiveChannels();
    const filteredChannels = this.filterChannels(activeChannels);

    if (isEmpty(filteredChannels)) {
      return null;
    }

    const labelKey = !isEmpty(filterValue) ? 'select_all_displayed' : 'select_all';

    return (
      <div className={id}>
        <SOVQueriesInputCheckbox
          items={[{
            id,
            label: __t(`${MODAL_MSG_KEY}.labels.${labelKey}`),
            isSelected: allChecked
          }]}
          highlightedId={id}
          onCheckboxChange={this.handleToggleAll}
        />
      </div>
    );
  }

  renderCheckboxList(filteredChannels: Array<CheckboxesListItem>): React.ReactNode {
    return (
      <SOVQueriesInputCheckbox
        items={this.highlightMatches(filteredChannels)}
        onCheckboxChange={this.handleToggleItem}
      />
    );
  }

  renderCheckboxes(): React.ReactNode {
    const { isLoading } = this.props;

    if (isLoading) {
      return this.renderSpinner();
    }

    const activeChannels = this.getActiveChannels();
    const filteredChannels = this.filterChannels(activeChannels);

    return (
      <React.Fragment>
        {this.renderSelectAll()}
        {
          isEmpty(filteredChannels)
            ? this.renderNotFound()
            : this.renderCheckboxList(filteredChannels)
        }
      </React.Fragment>
    );
  }

  renderFilter(): React.ReactNode {
    const { filterValue } = this.state;

    return (
      <div className="sov-queries-modal-step-related-channels__filter">
        <SOVQueriesInputFilter
          value={filterValue}
          onChange={this.handleFilterChange}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="sov-queries-modal-step-related-channels">
        {this.renderFilter()}
        <div className="sov-queries-modal-step-related-channels__checkboxes">
          {this.renderCheckboxes()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (
  { sovQueriesChannelsConsumerInsights, sovQueriesSearchResultsConsumerInsights }
): StateProps => ({
  results: sovQueriesSearchResultsConsumerInsights.results,
  resultsCustom: sovQueriesSearchResultsConsumerInsights.resultsCustom,
  channels: sovQueriesChannelsConsumerInsights.channels,
  isLoading: sovQueriesChannelsConsumerInsights.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchChannels: (
    reportId: number,
    queryId: number | undefined | null,
    preparedResults: Partial<SOVQuerySearchResultsEndpoint>,
    stepType: SOVQueryStepType
  ) => (
    dispatch(fetchChannels(reportId, queryId, preparedResults, stepType))
  ),

  dispatchUpdateFilteredChannels: (filteredChannels: Array<CheckboxesListItem>, stepType: SOVQueryStepType) => (
    dispatch(updateFilteredChannels(filteredChannels, stepType))
  ),

  dispatchUpdateNonFilteredChannels: (item: CheckboxesListItem, stepType: SOVQueryStepType, statusOverride?: boolean | null) => (
    dispatch(updateNonFilteredChannels(item, stepType, statusOverride))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVQueriesModalStepRelatedChannels);
